// hooks/useWebSocket.js - PRODUCTION READY WITH ANALYZER BROADCASTS
import { useState, useEffect, useRef, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 10;
const WS_PING_INTERVAL = 30000;

export const useWebSocket = (apiBaseUrl, onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const onMessageRef = useRef(onMessage);
  const connectionStartTime = useRef(null);
  const messageTimings = useRef([]);

  // Keep callback ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    try {
      connectionStartTime.current = performance.now();

      // ✅ FIX: Always derive WebSocket host from apiBaseUrl, not window.location
      // This ensures WebSocket connects to the correct backend server regardless of
      // what port the frontend is served from
      
      // Parse the apiBaseUrl to get host and port
      let wsHost;
      try {
        const apiUrl = new URL(apiBaseUrl);
        wsHost = apiUrl.host; // This includes port if specified
      } catch (e) {
        // Fallback: strip protocol manually
        wsHost = apiBaseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      }

      // Determine WebSocket protocol based on apiBaseUrl protocol
      const isSecure = apiBaseUrl.startsWith('https://');
      const protocol = isSecure ? 'wss:' : 'ws:';

      const wsUrl = `${protocol}//${wsHost}/ws/strategy-alerts/`;
      console.log(`[WS-PERF] ⏱️ Connecting to: ${wsUrl}`);
      console.log(`[WS-DEBUG] apiBaseUrl: ${apiBaseUrl}, derived wsHost: ${wsHost}`);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!mountedRef.current) return;

        const connectionTime = performance.now() - connectionStartTime.current;
        console.log(`[WS-PERF] ✅ Connected in ${connectionTime.toFixed(2)}ms`);

        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        const messageStartTime = performance.now();

        try {
          const data = JSON.parse(event.data);
          const messageType = data.type || 'unknown';

          // Skip connection/pong messages
          if (messageType === 'connection' || messageType === 'pong') {
            return;
          }

          const parseTime = performance.now() - messageStartTime;

          // Normalize data structure for different broadcast types
          const normalizedData = normalizeWebSocketMessage(data);

          // Log performance
          console.log(
            `[WS-PERF] 📨 ${messageType} parsed in ${parseTime.toFixed(2)}ms`,
            normalizedData.strategy_id ? `| Strategy: ${normalizedData.strategy_id}` : ''
          );

          // Call the message handler
          if (onMessageRef.current) {
            const callbackStartTime = performance.now();
            onMessageRef.current(normalizedData);
            const callbackTime = performance.now() - callbackStartTime;

            // Track slow callbacks
            if (callbackTime > 50) {
              console.warn(`[WS-PERF] ⚠️ Slow callback: ${callbackTime.toFixed(2)}ms`);
            }

            // Store timing for analysis
            messageTimings.current.push({
              type: messageType,
              parseTime,
              callbackTime,
              totalTime: parseTime + callbackTime,
            });

            // Keep last 100 timings
            if (messageTimings.current.length > 100) {
              messageTimings.current = messageTimings.current.slice(-100);
            }
          }
        } catch (err) {
          console.error('[WS] Parse error:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;

        console.log(`[WS] Disconnected (code: ${event.code})`);
        setIsConnected(false);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Log performance summary on disconnect
        if (messageTimings.current.length > 0) {
          const avgTime = messageTimings.current.reduce((a, b) => a + b.totalTime, 0) / messageTimings.current.length;
          console.log(`[WS-PERF] 📊 Session stats: ${messageTimings.current.length} messages, Avg: ${avgTime.toFixed(2)}ms`);
        }

        // Attempt reconnect for abnormal closures
        if (event.code !== 1000 && reconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          const delay = WS_RECONNECT_DELAY * Math.min(reconnectAttemptsRef.current, 5);

          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${WS_MAX_RECONNECT_ATTEMPTS})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              connect();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= WS_MAX_RECONNECT_ATTEMPTS) {
          console.error('[WS] Max reconnection attempts reached');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[WS] Connection error:', err);
    }
  }, [apiBaseUrl]);

  // Initialize connection
  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Send message to WebSocket
  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  // Force reconnect
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  return {
    isConnected,
    send,
    reconnect,
  };
};

/**
 * Normalize WebSocket message from broadcaster to consistent format
 */
function normalizeWebSocketMessage(data) {
  const messageType = data.type || 'unknown';

  // Base normalized structure
  const normalized = {
    type: messageType,
    timestamp: data.timestamp || new Date().toISOString(),
    raw: data,
  };

  // Extract strategy_id from various locations
  normalized.strategy_id = data.strategy_id || data.data?.strategy_id || data.bulk_strategy_id;
  normalized.bulk_strategy_id = normalized.strategy_id;

  // Handle state_sync (all strategies broadcast from /analyze/all/)
  if (messageType === 'state_sync' && data.data) {
    normalized.active_strategies = data.data.active_strategies || [];
    normalized.summary = data.data.summary || {};
    return normalized;
  }

  // Handle strategy_update
  if (messageType === 'strategy_update' && data.data) {
    Object.assign(normalized, {
      status: data.data.status,
      completion_percentage: data.data.completion_percentage,
      entry_time: data.data.entry_time,
      entry_price: data.data.entry_price,
      exit_time: data.data.exit_time,
      exit_price: data.data.exit_price,
      pnl: data.data.pnl,
      confirmations: data.data.confirmations,
      summary: data.data.summary,
      latest_trade: data.data.latest_trade,
    });
    return normalized;
  }

  // Handle bulk_entry
  if (messageType === 'bulk_entry' && data.data) {
    Object.assign(normalized, {
      status: 'open',
      completion_percentage: 100,
      entry_time: data.data.entry_time,
      entry_price: data.data.entry_price,
      direction: data.data.direction,
      confirmations: data.data.confirmations,
      required_signal: data.data.required_signal,
    });
    return normalized;
  }

  // Handle bulk_exit
  if (messageType === 'bulk_exit' && data.data) {
    Object.assign(normalized, {
      status: 'completed',
      entry_time: data.data.entry_time,
      entry_price: data.data.entry_price,
      exit_time: data.data.exit_time,
      exit_price: data.data.exit_price,
      pnl: data.data.pnl,
      pnl_pct: data.data.pnl_pct,
      direction: data.data.direction,
    });
    return normalized;
  }

  // Handle pending_update
  if (messageType === 'pending_update' && data.data) {
    Object.assign(normalized, {
      status: 'pending',
      completion_percentage: data.data.completion_percentage,
      trigger_signal: data.data.trigger_signal,
      pending_confirmations: data.data.pending_confirmations || [],
      required_signal: data.data.required_signal,
      satisfied_confirmations: data.data.satisfied_confirmations,
    });
    return normalized;
  }

  // Handle analysis_complete
  if (messageType === 'analysis_complete' && data.data) {
    Object.assign(normalized, {
      status: data.data.status,
      summary: data.data.summary,
      latest_trade: data.data.latest_trade,
    });
    return normalized;
  }

  // Fallback: merge data.data if present
  if (data.data) {
    Object.assign(normalized, data.data);
  }

  return normalized;
}

export default useWebSocket;
